import type {
  AnalysisConfig,
  CountingLine,
  CountingLineEventType,
  IntervalZone,
  IntervalZoneEventType,
} from "~/entities/analysis-config/types";
import axiosInstance from "~/shared/api/axios-instance";

interface CountingLineResponse {
  id: number;
  key: string;
  analysis_setting_status: {
    name: string;
    event_type: string;
    coordinates: { x: number; y: number }[];
  } | null;
  floor_plan_status: {
    coordinates: { x: number; y: number }[];
  } | null;
  pseudo_camera_orientation_id: number;
}

interface IntervalZoneResponse {
  id: number;
  key: string;
  analysis_setting_status: {
    name: string;
    event_type: string;
    coordinates: { x: number; y: number }[];
  } | null;
  floor_plan_status: {
    coordinates: { x: number; y: number }[];
  } | null;
  pseudo_camera_orientation_id: number;
}

interface AnalysisConfigResponse {
  pseudo_camera_orientation_id: number;
  pseudo_camera_id: number;
  analyzed_id?: string;
  floor_plan_id?: number;
  preview_img_url?: string;
  counting_lines: CountingLineResponse[];
  interval_zones: IntervalZoneResponse[];
}

function convertCountingLine(resp: CountingLineResponse): CountingLine {
  return {
    id: resp.id,
    key: resp.key,
    cameraId: resp.pseudo_camera_orientation_id,
    analysisConfig: resp.analysis_setting_status
      ? {
          name: resp.analysis_setting_status.name,
          eventType: resp.analysis_setting_status.event_type as CountingLineEventType,
          coordinates: resp.analysis_setting_status.coordinates,
        }
      : null,
    floorPlanConfig: resp.floor_plan_status
      ? { coordinates: resp.floor_plan_status.coordinates }
      : null,
  };
}

function convertIntervalZone(resp: IntervalZoneResponse): IntervalZone {
  return {
    id: resp.id,
    key: resp.key,
    cameraId: resp.pseudo_camera_orientation_id,
    analysisConfig: resp.analysis_setting_status
      ? {
          name: resp.analysis_setting_status.name,
          eventType: resp.analysis_setting_status.event_type as IntervalZoneEventType,
          coordinates: resp.analysis_setting_status.coordinates,
        }
      : null,
    floorPlanConfig: resp.floor_plan_status
      ? { coordinates: resp.floor_plan_status.coordinates }
      : null,
  };
}

export async function fetchAnalysisConfigs(
  branchUid: string,
  versionId?: number,
): Promise<AnalysisConfig[]> {
  const { data } = await axiosInstance.get<AnalysisConfigResponse[]>(
    `branches/${branchUid}/analysis-settings/`,
    { params: { version: versionId } },
  );

  return data.map((item) => ({
    cameraId: item.pseudo_camera_orientation_id,
    pseudoCameraId: item.pseudo_camera_id,
    previewImageUrl: item.preview_img_url,
    analysisNumber: item.analyzed_id ?? null,
    floorPlanId: item.floor_plan_id ?? null,
    countingLines: item.counting_lines.map(convertCountingLine),
    intervalZones: item.interval_zones.map(convertIntervalZone),
  }));
}
